/**
 * Utility to apply portfolio colors dynamically via CSS variables
 * This function updates CSS custom properties based on portfolio settings
 */

// Cache to prevent unnecessary DOM manipulations
let lastAppliedColors = null;

export function applyPortfolioColors(settings) {
  if (!settings) return;

  const root = document.documentElement;

  // Map settings to CSS variables with defaults
  const colorMap = {
    primary_color: settings.primary_color || '#4e69c3',
    secondary_color: settings.secondary_color || '#5e87f5',
    button_color: settings.button_color || '#3d4b91',
    button_hover_color: settings.button_hover_color || '#4e69c3',
    quote_color: settings.quote_color || '#5e87f5',
    tech_icon_border_color: settings.tech_icon_border_color || '#4e69c3',
    project_card_shadow_color: settings.project_card_shadow_color || '#5e87f5',
    scrollbar_color: settings.scrollbar_color || '#b279d8',
    scrollbar_hover_color: settings.scrollbar_hover_color || '#de82eb',
  };

  // Check if colors have actually changed
  const colorString = JSON.stringify(colorMap);
  if (lastAppliedColors === colorString) {
    return; // Skip if colors haven't changed
  }
  lastAppliedColors = colorString;

  // Apply colors to CSS variables
  root.style.setProperty('--imp-text-color', colorMap.primary_color);
  root.style.setProperty('--primary-color', colorMap.primary_color);
  root.style.setProperty('--secondary-color', colorMap.secondary_color);
  root.style.setProperty('--button-color', colorMap.button_color);
  root.style.setProperty('--button-hover-color', colorMap.button_hover_color);
  root.style.setProperty('--quote-color', colorMap.quote_color);
  root.style.setProperty('--tech-icon-border-color', colorMap.tech_icon_border_color);
  root.style.setProperty('--project-card-shadow-color', colorMap.project_card_shadow_color);
  root.style.setProperty('--scrollbar-color', colorMap.scrollbar_color);
  root.style.setProperty('--scrollbar-hover-color', colorMap.scrollbar_hover_color);

  // Apply colors to existing CSS classes that use hardcoded colors
  // Update .blockquote-footer
  const blockquoteFooterStyle = document.createElement('style');
  blockquoteFooterStyle.id = 'dynamic-colors-blockquote';
  blockquoteFooterStyle.textContent = `
    .blockquote-footer {
      color: var(--quote-color, ${colorMap.quote_color}) !important;
    }
  `;
  const existingBlockquote = document.getElementById('dynamic-colors-blockquote');
  if (existingBlockquote) existingBlockquote.remove();
  document.head.appendChild(blockquoteFooterStyle);

  // Update button styles
  const buttonStyle = document.createElement('style');
  buttonStyle.id = 'dynamic-colors-buttons';
  buttonStyle.textContent = `
    .btn-primary {
      background-color: var(--button-color, ${colorMap.button_color}) !important;
      border-color: var(--button-color, ${colorMap.button_color}) !important;
    }
    .btn-primary:hover {
      background-color: var(--button-hover-color, ${colorMap.button_hover_color}) !important;
      border-color: var(--button-hover-color, ${colorMap.button_hover_color}) !important;
    }
  `;
  const existingButton = document.getElementById('dynamic-colors-buttons');
  if (existingButton) existingButton.remove();
  document.head.appendChild(buttonStyle);

  // Update project card shadow
  const projectCardStyle = document.createElement('style');
  projectCardStyle.id = 'dynamic-colors-project-cards';
  projectCardStyle.textContent = `
    .project-card-view {
      box-shadow: 0 4px 5px 3px var(--project-card-shadow-color, ${colorMap.project_card_shadow_color}) !important;
    }
    .project-card-view:hover {
      box-shadow: 0 4px 4px 5px var(--project-card-shadow-color, ${colorMap.project_card_shadow_color}) !important;
    }
  `;
  const existingProjectCard = document.getElementById('dynamic-colors-project-cards');
  if (existingProjectCard) existingProjectCard.remove();
  document.head.appendChild(projectCardStyle);

  // Update tech icon border
  const techIconStyle = document.createElement('style');
  techIconStyle.id = 'dynamic-colors-tech-icons';
  techIconStyle.textContent = `
    .tech-icons {
      border: 1.7px solid var(--tech-icon-border-color, ${colorMap.tech_icon_border_color}cc) !important;
      box-shadow: 4px 5px 4px 3px rgba(94, 135, 245, 0.137) !important;
    }
  `;
  const existingTechIcon = document.getElementById('dynamic-colors-tech-icons');
  if (existingTechIcon) existingTechIcon.remove();
  document.head.appendChild(techIconStyle);

  // Update scrollbar colors
  const scrollbarStyle = document.createElement('style');
  scrollbarStyle.id = 'dynamic-colors-scrollbar';
  scrollbarStyle.textContent = `
    ::-webkit-scrollbar-thumb {
      background: var(--scrollbar-color, ${colorMap.scrollbar_color}) !important;
    }
    ::-webkit-scrollbar-thumb:hover {
      background: var(--scrollbar-hover-color, ${colorMap.scrollbar_hover_color}) !important;
    }
  `;
  const existingScrollbar = document.getElementById('dynamic-colors-scrollbar');
  if (existingScrollbar) existingScrollbar.remove();
  document.head.appendChild(scrollbarStyle);
}

